"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { useState } from "react";
function HeatBar({
  level
}: {
  level: number;
}) {
  return <div className="flex items-center gap-[3px]" aria-label={`Security level ${level} of 5`}>
      {[0, 1, 2, 3, 4].map(i => <span key={i} data-index={i} className="h-[6px] w-[18px]" style={{
      backgroundColor: i < level ? "var(--primary)" : "var(--border)"
    }} />)}
    </div>;
}
function RecipeCardsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="rounded-md border animate-pulse" style={{
        backgroundColor: "var(--card)",
        borderColor: "var(--border)"
      }}>
            <div className="aspect-[4/3] w-full" style={{
          backgroundColor: "var(--muted)"
        }} />
            <div className="p-6 space-y-3">
              <div className="h-3 w-2/3" style={{
            backgroundColor: "var(--muted)"
          }} />
              <div className="h-6 w-full" style={{
            backgroundColor: "var(--muted)"
          }} />
              <div className="h-4 w-full" style={{
            backgroundColor: "var(--muted)"
          }} />
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <p className="text-sm" style={{
      color: "var(--muted-foreground)"
    }}>
        Unable to load articles right now.
      </p>;
  }
  if (!posts || posts.length === 0) {
    return <p className="text-sm" style={{
      color: "var(--muted-foreground)"
    }}>
        No articles to display yet.
      </p>;
  }
  const meta = [{
    time: "3 MIN",
    serves: "BASIC",
    heat: 4
  }, {
    time: "5 MIN",
    serves: "ADVANCED",
    heat: 3
  }, {
    time: "8 MIN",
    serves: "EXPERT",
    heat: 5
  }, {
    time: "2 MIN",
    serves: "BASIC",
    heat: 2
  }, {
    time: "4 MIN",
    serves: "INTERM",
    heat: 4
  }, {
    time: "6 MIN",
    serves: "ADVANCED",
    heat: 3
  }];
  return <div className="grid grid-cols-1 gap-7 sm:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => {
      const m = meta[i % meta.length];
      const excerpt = post.excerpt ? post.excerpt.replace(/<[^>]+>/g, "").trim() : "";
      return <article key={post.id} data-index={i} className="group flex flex-col overflow-hidden rounded-md border transition-colors duration-[250ms]" style={{
        backgroundColor: "var(--card)",
        borderColor: "var(--border)"
      }} onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.borderColor = "var(--foreground)";
      }} onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.borderColor = "var(--border)";
      }}>
            <Link to={post.link || "#"} className="block overflow-hidden">
              <div className="aspect-[4/3] w-full overflow-hidden" style={{
            backgroundColor: "var(--muted)"
          }}>
                {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover grayscale transition-all duration-[350ms] ease-out group-hover:grayscale-0" style={{
              filter: "sepia(15%) grayscale(100%)"
            }} onMouseEnter={e => {
              (e.currentTarget as HTMLImageElement).style.filter = "none";
            }} onMouseLeave={e => {
              (e.currentTarget as HTMLImageElement).style.filter = "sepia(15%) grayscale(100%)";
            }} /> : <div className="h-full w-full" style={{
              background: "linear-gradient(135deg, var(--secondary), var(--muted))"
            }} />}
              </div>
            </Link>

            <div className="flex flex-1 flex-col p-6">
              {}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] font-medium uppercase" style={{
            color: "var(--foreground)",
            letterSpacing: "0.02em"
          }}>
                <span>{m.time}</span>
                <span aria-hidden="true" style={{
              color: "var(--border)"
            }}>{"|"}</span>
                <span>{m.serves}</span>
                <span aria-hidden="true" style={{
              color: "var(--border)"
            }}>{"|"}</span>
                <HeatBar level={m.heat} />
              </div>

              <h3 className="mt-4 font-default text-lg font-semibold leading-snug line-clamp-2" style={{
            color: "var(--foreground)"
          }}>
                <Link to={post.link || "#"} className="hover:opacity-80 transition-opacity">
                  {post.title}
                </Link>
              </h3>

              {excerpt && <p className="mt-2 text-sm leading-relaxed line-clamp-1" style={{
            color: "color-mix(in srgb, var(--foreground) 75%, transparent)"
          }}>
                  {excerpt}
                </p>}

              <div className="mt-5 pt-1">
                <Link to={post.link || "#"} className="group/link inline-flex items-center gap-1.5 text-sm font-medium" style={{
              color: "var(--foreground)"
            }}>
                  <span className="border-b transition-all duration-[150ms]" style={{
                borderBottomWidth: "1px",
                borderColor: "var(--foreground)"
              }}>
                    Read article
                  </span>
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </article>;
    })}
    </div>;
}
export default function Relatedvpnarticles() {
  const [activeFilter, setActiveFilter] = useState("All");
  const filters = ["All", "Guides", "News"];
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="31484" id="relatedvpnarticles" data-nav="light" className="w-full py-24 md:py-32 bg-background text-foreground">
      <div className="mx-auto max-w-[1360px] px-5 md:px-8">
        {}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-8">
            <p className="mb-4 font-sans text-[13px] font-semibold uppercase" style={{
            color: "color-mix(in srgb, var(--foreground) 70%, transparent)",
            letterSpacing: "0.18em"
          }}>
              Security Insights
            </p>
            <h2 className="font-serif font-medium leading-[1.02] tracking-tight" style={{
            color: "var(--foreground)",
            fontSize: "clamp(2rem, 3.4vw + 0.75rem, 4rem)",
            letterSpacing: "-0.025em"
          }}>
              Latest{" "}
              <span className="italic" style={{
              color: "var(--primary)",
              fontStyle: "italic"
            }}>
                updates
              </span>
              .
            </h2>
          </div>

          <div className="flex flex-col items-start gap-5 lg:col-span-4 lg:items-end">
            <Link to="/BlogsPage" className="inline-flex items-center gap-1.5 text-sm font-medium" style={{
            color: "var(--foreground)"
          }}>
              <span className="border-b" style={{
              borderBottomWidth: "1px",
              borderColor: "var(--foreground)"
            }}>
                View all posts
              </span>
              <ArrowRight className="h-4 w-4" />
            </Link>

            <div className="flex flex-wrap items-center gap-2">
              {filters.map((f, i) => {
              const active = f === activeFilter;
              return <Button key={f} data-index={i} type="button" variant="outline" size="sm" onClick={() => setActiveFilter(f)} className="rounded-lg border px-4 h-8 text-xs font-semibold uppercase tracking-wider transition-colors" style={active ? {
                backgroundColor: "var(--primary)",
                color: "var(--primary-foreground)",
                borderColor: "var(--primary)",
                letterSpacing: "0.08em"
              } : {
                backgroundColor: "transparent",
                color: "var(--foreground)",
                borderColor: "var(--foreground)",
                letterSpacing: "0.08em"
              }}>
                    {f}
                  </Button>;
            })}
            </div>
          </div>
        </div>

        {}
        <div className="mt-10 mb-12 h-px w-full" style={{
        backgroundColor: "var(--border)"
      }} />

        {}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date",
        status: ["publish"]
      }}>
          <RecipeCardsConsumer />
        </PostsProvider>
      </div>
    </section>;
}